import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Pause, Play, X } from "lucide-react";
import { img, fmtDate } from "@/lib/community";

const INTERVAL = 4500;

export function Slideshow({ photos, title, onClose }) {
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(true);
  const touch = useRef(null);
  const n = photos.length;
  const go = useCallback((d) => setI((x) => (x + d + n) % n), [n]);

  useEffect(() => {
    if (!playing || n < 2) return undefined;
    const t = setTimeout(() => go(1), INTERVAL);
    return () => clearTimeout(t);
  }, [i, playing, n, go]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === " ") { e.preventDefault(); setPlaying((p) => !p); }
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [go, onClose]);

  useEffect(() => {
    if (photos[(i + 1) % n]) new Image().src = img(photos[(i + 1) % n].url);
  }, [i, n, photos]);

  const p = photos[i];
  if (!p) return null;
  const arrow = "absolute top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-[#FBF8F3]/10 hover:bg-[#FBF8F3]/25 text-[#FBF8F3] flex items-center justify-center transition-colors";
  return (
    <div className="fixed inset-0 z-[100] bg-[#1c1816] flex flex-col" data-testid="album-slideshow"
      onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touch.current == null) return;
        const dx = e.changedTouches[0].clientX - touch.current;
        if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
        touch.current = null;
      }}>
      <div className="flex items-center justify-between px-5 md:px-10 py-4 text-[#FBF8F3]">
        <div>
          <p className="jc-eyebrow !text-[#E7C4BC]">Community Wall</p>
          <p className="jc-display text-2xl md:text-3xl mt-1">{title}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setPlaying((x) => !x)} className="w-11 h-11 rounded-full hover:bg-white/10 flex items-center justify-center" aria-label={playing ? "Pause" : "Play"} data-testid="slideshow-play-toggle">
            {playing ? <Pause className="w-5 h-5" strokeWidth={1.25} /> : <Play className="w-5 h-5" strokeWidth={1.25} />}
          </button>
          <button onClick={onClose} className="w-11 h-11 rounded-full hover:bg-white/10 flex items-center justify-center" aria-label="Close slideshow" data-testid="slideshow-close-btn">
            <X className="w-5 h-5" strokeWidth={1.25} />
          </button>
        </div>
      </div>
      <div className="relative flex-1 min-h-0 flex items-center justify-center px-2 md:px-20">
        <img key={p.id} src={img(p.url)} alt={p.caption || ""} className="max-h-full max-w-full object-contain jc-fade" data-testid="slideshow-image" />
        {n > 1 && (
          <>
            <button onClick={() => go(-1)} className={`${arrow} left-3 md:left-8`} aria-label="Previous photo" data-testid="slideshow-prev-btn"><ChevronLeft className="w-6 h-6" strokeWidth={1.25} /></button>
            <button onClick={() => go(1)} className={`${arrow} right-3 md:right-8`} aria-label="Next photo" data-testid="slideshow-next-btn"><ChevronRight className="w-6 h-6" strokeWidth={1.25} /></button>
          </>
        )}
      </div>
      <div className="px-5 md:px-10 py-5 text-center text-[#FBF8F3]">
        {p.caption && <p className="font-serif text-lg md:text-xl" data-testid="slideshow-caption">{p.caption}</p>}
        <p className="text-[11px] uppercase tracking-[0.25em] text-[#FBF8F3]/60 mt-2" data-testid="slideshow-counter">
          {i + 1} / {n}{p.date ? ` · ${fmtDate(p.date)}` : ""}
        </p>
        {n > 1 && (
          <div className="mt-4 h-px bg-white/15 max-w-xs mx-auto overflow-hidden">
            <div className="h-full bg-[#C9A97A]" style={{ width: `${((i + 1) / n) * 100}%`, transition: "width 0.5s ease" }} />
          </div>
        )}
      </div>
    </div>
  );
}
